import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Baby, Cake, Sparkles, Building2, TreePine, Gift, Target, Gem, Leaf } from 'lucide-react';
import SEO from '../components/SEO';
import './ServicioSEO.css';

const RegalosPersonalizados = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Regalos Personalizados",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Artelaser3D",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Asturias",
        "addressRegion": "Asturias",
        "addressCountry": "ES"
      }
    },
    "areaServed": [
      { "@type": "City", "name": "Oviedo" },
      { "@type": "City", "name": "Gijón" },
      { "@type": "City", "name": "Avilés" },
      { "@type": "Country", "name": "España" }
    ],
    "description": "Regalos personalizados en Asturias. Grabado láser e impresión 3D para ocasiones especiales. Bodas, bautizos, cumpleaños, aniversarios y eventos corporativos.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock"
    }
  };

  const occasions = [
    {
      title: 'Bodas',
      description: 'Detalles únicos para el día más especial: invitaciones, recuerdos, decoración y regalos para los novios.',
      icon: <Heart size={48} />,
      examples: ['Invitaciones en madera', 'Cajas para alianzas', 'Etiquetas para detalles', 'Carteles de bienvenida']
    },
    {
      title: 'Bautizos y Comuniones',
      description: 'Recuerdos personalizados que los invitados atesorarán. Dulceros, portafotos y mucho más.',
      icon: <Baby size={48} />,
      examples: ['Portafotos personalizados', 'Recuerdos grabados', 'Cajas de recuerdos', 'Marcos decorativos']
    },
    {
      title: 'Cumpleaños',
      description: 'Desde el primer cumpleaños hasta los más especiales. Tartas decorativas, letreros y regalos únicos.',
      icon: <Cake size={48} />,
      examples: ['Topper para tartas', 'Letras luminosas', 'Cajas de regalo', 'Decoración temática']
    },
    {
      title: 'Aniversarios',
      description: 'Celebra los años de amor con regalos que cuentan vuestra historia. Grabados con fecha y dedicatoria.',
      icon: <Sparkles size={48} />,
      examples: ['Cuadros conmemorativos', 'Árboles de firmas', 'Placas grabadas', 'Álbumes personalizados']
    },
    {
      title: 'Eventos Corporativos',
      description: 'Regalos empresariales que reflejan la identidad de tu marca. Para clientes, empleados y partners.',
      icon: <Building2 size={48} />,
      examples: ['Artículos con logo', 'Trofeos corporativos', 'Agendas personalizadas', 'Kits de bienvenida']
    },
    {
      title: 'Navidad',
      description: 'Decoraciones únicas y regalos personalizados para las fiestas. Adornos, belenes y más.',
      icon: <TreePine size={48} />,
      examples: ['Adornos navideños', 'Belenes personalizados', 'Calendarios de adviento', 'Etiquetas para regalos']
    }
  ];

  const products = [
    {
      name: 'Cuadros Decorativos',
      description: 'Personalizados con nombres, fechas, frases o ilustraciones. En madera o acrílico.',
      price: 'Desde 25€'
    },
    {
      name: 'Cajas Grabadas',
      description: 'Para joyas, recuerdos, o simplemente decorar. Grabado personalizado en tapa.',
      price: 'Desde 20€'
    },
    {
      name: 'Llaveros Personalizados',
      description: 'En madera, acrílico o cuero. Formas originales y grabado de alta calidad.',
      price: 'Desde 8€'
    },
    {
      name: 'Figuras 3D',
      description: 'Miniaturas personalizadas, bustos, o figuras de personajes favoritos.',
      price: 'Desde 30€'
    },
    {
      name: 'Lámparas Personalizadas',
      description: 'Lámparas de mesa con diseños grabados o impresos en 3D. Ambiente único.',
      price: 'Desde 35€'
    },
    {
      name: 'Placas Conmemorativas',
      description: 'Para celebrar momentos especiales. Grabado profundo y duradero.',
      price: 'Desde 15€'
    }
  ];

  return (
    <>
      <SEO 
        title="Regalos Personalizados en Asturias"
        description="Regalos personalizados con grabado láser e impresión 3D en Asturias. Bodas, bautizos, cumpleaños, eventos corporativos. Envíos a toda España."
        keywords="regalos personalizados Asturias, regalos grabados Asturias, regalos boda Asturias, regalos personalizados Oviedo, regalos personalizados Gijón, regalos empresa Asturias"
        canonicalUrl="https://artelaser3d.es/regalos-personalizados-asturias"
        schema={schema}
      />

      <section className="page-hero page-hero--gifts">
        <div className="page-hero__container">
          <span className="page-hero__badge">Regalos Personalizados</span>
          <h1 className="page-hero__title">
            Regalos únicos hechos con
            <span className="text-primary"> cariño</span> en Asturias
          </h1>
          <p className="page-hero__description">
            Crea regalos personalizados inolvidables para cualquier ocasión. 
            Grabado láser e impresión 3D para momentos que merecen algo especial.
          </p>
          <div className="page-hero__cta">
            <Link to="/proyecto-personalizado" className="btn btn--white btn--lg">
              Crear Regalo Personalizado
            </Link>
          </div>
        </div>
      </section>

      <section className="section section--occasions section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Ocasiones</span>
            <h2>Regalos para cada momento especial</h2>
            <p className="section__description">
              Desde celebraciones familiares hasta eventos corporativos, 
              tenemos el regalo perfecto para cada ocasión.
            </p>
          </header>

          <div className="occasions-grid">
            {occasions.map((occasion, index) => (
              <article key={index} className="occasion-card">
                <span className="occasion-card__icon">{occasion.icon}</span>
                <h3>{occasion.title}</h3>
                <p>{occasion.description}</p>
                
                <div className="occasion-card__examples">
                  <strong>Ideas:</strong>
                  <ul>
                    {occasion.examples.map((example, i) => (
                      <li key={i}>{example}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--products section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Productos</span>
            <h2>Nuestros regalos estrella</h2>
            <p className="section__description">
              Estos son algunos de nuestros productos más solicitados. 
              Todos personalizables a tu gusto.
            </p>
          </header>

          <div className="products-grid">
            {products.map((product, index) => (
              <article key={index} className="product-card">
                <h3>{product.name}</h3>
                <p>{product.description}</p>
                <span className="product-card__price">{product.price}</span>
              </article>
            ))}
          </div>

          <div className="section__cta">
            <p className="text-gray">* Los precios son orientativos y varían según personalización y cantidad</p>
          </div>
        </div>
      </section>

      <section className="section section--why-custom section--visible">
        <div className="section__container">
          <div className="why-custom-grid">
            <div className="why-custom-content">
              <span className="section__badge">Ventajas</span>
              
              <h2>¿Por qué elegir regalos personalizados?</h2>
              
              <ul className="why-custom-list">
                <li>
                  <div className="why-custom-list__icon"><Gift size={20} /></div>
                  <div className="why-custom-list__content">
                    <strong>Originalidad:</strong> 
                    Regalos que no encontrarás en ninguna tienda
                  </div>
                </li>
                <li>
                  <div className="why-custom-list__icon"><Heart size={20} /></div>
                  <div className="why-custom-list__content">
                    <strong>Significado:</strong> 
                    Demuestras que has pensado en la persona
                  </div>
                </li>
                <li>
                  <div className="why-custom-list__icon"><Sparkles size={20} /></div>
                  <div className="why-custom-list__content">
                    <strong>Exclusividad:</strong> 
                    Cada pieza es única y irrepetible
                  </div>
                </li>
                <li>
                  <div className="why-custom-list__icon"><Target size={20} /></div>
                  <div className="why-custom-list__content">
                    <strong>A medida:</strong> 
                    Adaptamos el diseño a tus preferencias exactas
                  </div>
                </li>
                <li>
                  <div className="why-custom-list__icon"><Gem size={20} /></div>
                  <div className="why-custom-list__content">
                    <strong>Calidad:</strong> 
                    Materiales premium y acabados profesionales
                  </div>
                </li>
                <li>
                  <div className="why-custom-list__icon"><Leaf size={20} /></div>
                  <div className="why-custom-list__content">
                    <strong>Durabilidad:</strong> 
                    Regalos que duran para siempre
                  </div>
                </li>
              </ul>
            </div>

            <div className="why-custom-visual">
              <div className="why-custom-image">
                <img 
                  src="/images/pexels-jakubzerdzicki-31121900.jpg" 
                  alt="Regalos únicos y personalizados grabados con láser e impresión 3D en Asturias"
                  className="why-custom-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--cta section--cta--regalos section--visible">
        <div className="cta__content">
          <h2 className="cta__title--regalos">¿Tienes una idea para un regalo?</h2>
          <p className="cta__description--regalos">
            Cuéntanos tu idea y la haremos realidad. Asesoramiento gratuito.
          </p>
          <div className="cta__buttons">
            <Link to="/proyecto-personalizado" className="btn btn--primary btn--lg">
              Crear Mi Regalo Personalizado
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default RegalosPersonalizados;
